<template>
  <v-container fluid grid-list-lg>
    <v-layout column wrap>
      <v-flex d-flex xs12>
        <v-layout row wrap align-center justify-center>
          <v-flex xs12 sm5 d-flex class="title">Messaging</v-flex>
          <v-spacer />
        </v-layout>
      </v-flex>

      <v-tabs v-model="tab" class="mt-2">
        <v-tab>Send SMS</v-tab>
        <v-tab>Email Templates</v-tab>
        <v-tab>Send Email Blast</v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <!-- ─── SMS ─── -->
        <v-tab-item>
          <v-layout row wrap align-center justify-center class="mt-4">
            <v-flex xs8>
              <v-text-field
                v-model="phoneNo"
                clearable
                prepend-icon="phone"
                label="Phone no e.g. +66812345678"
                single-line
              />
              <v-textarea label="Body" auto-grow v-model="smsBody" clearable />
              <v-btn @click="submitSms" color="accent">Send</v-btn>
            </v-flex>
          </v-layout>
        </v-tab-item>

        <!-- ─── EMAIL TEMPLATES ─── -->
        <v-tab-item>
          <v-row class="mt-4 mb-4" align="center">
            <v-col>
              <span class="subtitle-1 font-weight-medium">Email Blast Templates</span>
            </v-col>
            <v-col cols="auto">
              <v-btn color="primary" @click="$router.push('/email-blast/templates/new')">
                <v-icon left>mdi-plus</v-icon>
                New Template
              </v-btn>
            </v-col>
          </v-row>

          <v-card>
            <v-data-table
              :headers="templateHeaders"
              :items="templates"
              :loading="templatesLoading"
              loading-text="Loading templates..."
              no-data-text="No templates yet. Create your first one!"
            >
              <template #item.active="{ item }">
                <v-chip :color="item.active ? 'success' : 'grey'" small label text-color="white">
                  {{ item.active ? 'Active' : 'Inactive' }}
                </v-chip>
              </template>
              <template #item.createdAt="{ item }">
                {{ formatDate(item.createdAt) }}
              </template>
              <template #item.actions="{ item }">
                <v-btn
                  x-small
                  outlined
                  color="primary"
                  class="mr-2"
                  @click="$router.push(`/email-blast/templates/${item.id}/edit`)"
                  >Edit</v-btn
                >
                <v-btn x-small outlined color="error" @click="confirmDelete(item)">Delete</v-btn>
              </template>
            </v-data-table>
          </v-card>

          <v-dialog v-model="deleteDialog" max-width="400">
            <v-card>
              <v-card-title>Delete template?</v-card-title>
              <v-card-text>
                This action cannot be undone.
                <strong v-if="deleteTarget">{{ deleteTarget.name }}</strong> will be permanently removed.
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn text @click="deleteDialog = false">Cancel</v-btn>
                <v-btn color="error" :loading="deleting" @click="handleDelete">Delete</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-tab-item>

        <!-- ─── SEND EMAIL BLAST ─── -->
        <v-tab-item>
          <div class="mt-4">
            <v-card v-if="blastResult" max-width="500">
              <v-card-title class="success--text">
                <v-icon color="success" left>mdi-check-circle</v-icon>
                Blast Sent!
              </v-card-title>
              <v-card-text>
                <div class="body-2">Total Targets: {{ blastResult.totalTargets }}</div>
                <div class="body-2">Sent: {{ blastResult.sentCount }}</div>
                <div v-if="blastResult.failedCount > 0" class="body-2 error--text">
                  Failed: {{ blastResult.failedCount }}
                </div>

                <v-divider class="my-3" />
                <div class="subtitle-2 mb-1">Breakdown</div>
                <div v-for="b in blastResult.blasts" :key="b.id" class="body-2">
                  • {{ b.label }} — Blast #{{ b.id }} ({{ b.status }}), sent {{ b.sentCount
                  }}<span v-if="b.failedCount > 0" class="error--text">, failed {{ b.failedCount }}</span>
                </div>
                <div v-for="(e, i) in blastResult.errors" :key="`err-${i}`" class="body-2 error--text">
                  • {{ e.label }} — failed: {{ e.message }}
                </div>
              </v-card-text>
              <v-card-actions>
                <v-btn color="primary" @click="blastResult = null">Send Another</v-btn>
              </v-card-actions>
            </v-card>

            <v-card v-else max-width="700">
              <v-card-text>
                <v-alert v-if="blastError" type="error" dense class="mb-4">{{ blastError }}</v-alert>

                <div class="subtitle-2 mb-2">Target *</div>
                <v-radio-group v-model="targetMode" row class="mt-0 mb-4">
                  <v-radio label="Organization IDs" value="org" />
                  <v-radio label="Specific User IDs" value="users" />
                </v-radio-group>

                <v-text-field
                  v-if="targetMode === 'org'"
                  v-model="orgIds"
                  label="Organization IDs (comma-separated) *"
                  outlined
                  dense
                  class="mb-2"
                  placeholder="1,2,3"
                />
                <v-text-field
                  v-else
                  v-model="userIds"
                  label="User IDs (comma-separated) *"
                  outlined
                  dense
                  class="mb-2"
                  placeholder="1,2,3"
                />

                <v-text-field
                  v-model="blastSubject"
                  label="Subject *"
                  outlined
                  dense
                  class="mb-2"
                  placeholder="Email subject"
                />

                <div class="subtitle-2 mb-2">Content *</div>
                <v-radio-group v-model="blastMode" row class="mt-0 mb-4">
                  <v-radio label="Use Template" value="template" />
                  <v-radio label="Custom HTML" value="html" />
                </v-radio-group>

                <template v-if="blastMode === 'template'">
                  <v-select
                    v-model="templateId"
                    :items="activeTemplates"
                    item-text="displayLabel"
                    item-value="id"
                    label="Template *"
                    outlined
                    dense
                    class="mb-2"
                  />
                  <v-textarea
                    v-model="blastVariables"
                    label="Variables (JSON, optional)"
                    outlined
                    dense
                    rows="3"
                    class="mb-2"
                    style="font-family: monospace"
                    placeholder='{"name": "John", "code": "ABC123"}'
                  />
                </template>

                <v-textarea
                  v-else
                  v-model="htmlContent"
                  label="HTML Content *"
                  outlined
                  dense
                  rows="10"
                  class="mb-2"
                  style="font-family: monospace"
                  placeholder="<h1>Hello</h1><p>Your email content here</p>"
                />

                <v-row>
                  <v-col>
                    <v-text-field v-model="blastFrom" label="From" outlined dense placeholder="help@ooca.co" />
                  </v-col>
                  <v-col>
                    <v-text-field v-model="blastReplyTo" label="Reply-To" outlined dense placeholder="optional" />
                  </v-col>
                </v-row>

                <v-btn color="primary" :loading="sending" :disabled="sending" @click="handleSend"> Send Blast </v-btn>
              </v-card-text>
            </v-card>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </v-layout>

    <!-- SMS invalid input dialog -->
    <v-dialog v-model="smsDialog" max-width="480">
      <v-card>
        <v-card-title class="text-h5">Invalid input!</v-card-title>
        <v-card-text>
          <ul>
            <li>1. Phone number must be in format: +66812345678</li>
            <li>2. Text message must not be empty.</li>
          </ul>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="accent darken-1" text @click="smsDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Blast sending progress modal (blocking) -->
    <v-dialog v-model="sending" persistent no-click-animation max-width="440">
      <v-card>
        <v-card-text class="text-center py-6">
          <v-progress-circular indeterminate color="primary" size="48" width="4" class="mb-4" />
          <div class="subtitle-1 font-weight-medium mb-4">Sending... ({{ progress.current }}/{{ progress.total }})</div>
          <v-alert type="warning" dense text class="mb-0 text-left">
            <div class="font-weight-bold mb-1">⚠️ กรุณาอย่าออกจากหน้านี้หรือปิดหน้าต่างจนกว่าการส่งจะเสร็จสิ้น</div>
            <div>Please do not leave this page or close the window until sending is complete.</div>
          </v-alert>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { Vue } from 'vue-property-decorator';
import axiosTemplate from '@/utils/axiosTemplate';
import store from '../../store';
import smsService from '../../services/sms';

export default Vue.extend({
  store,
  data: () => ({
    tab: 0,

    // SMS
    phoneNo: '',
    smsBody: '',
    smsDialog: false,

    // Templates
    templates: [],
    templatesLoading: false,
    deleteDialog: false,
    deleteTarget: null,
    deleting: false,
    templateHeaders: [
      { text: 'Name', value: 'name' },
      { text: 'Default Subject', value: 'subject' },
      { text: 'Status', value: 'active', sortable: false },
      { text: 'Created', value: 'createdAt' },
      { text: '', value: 'actions', sortable: false, align: 'end' },
    ],

    // Email Blast
    blastMode: 'template',
    templateId: null,
    blastVariables: '',
    htmlContent: '',
    blastSubject: '',
    targetMode: 'org',
    userIds: '',
    orgIds: '',
    blastFrom: 'help@ooca.co',
    blastReplyTo: '',
    sending: false,
    progress: { current: 0, total: 0 },
    blastResult: null,
    blastError: null,
  }),

  computed: {
    activeTemplates() {
      return this.templates
        .filter((t) => t.active)
        .map((t) => ({ ...t, displayLabel: `${t.name} (default subject: ${t.subject})` }));
    },
  },

  watch: {
    tab(val) {
      if (val === 1 && this.templates.length === 0) this.fetchTemplates();
      if (val === 2 && this.templates.length === 0) this.fetchTemplates();
    },
  },

  methods: {
    // ── SMS ──
    submitSms() {
      if (!/\+[0-9]+/.test(this.phoneNo) || !this.smsBody || !this.smsBody.trim()) {
        this.smsDialog = true;
        return;
      }
      this.$root
        .$confirm('Send SMS', `Are you sure you want to send: "${this.smsBody}" to "${this.phoneNo}"`, {
          color: 'primary',
          submitText: 'Confirm',
        })
        .then((confirm) => {
          if (confirm) {
            smsService
              .send(this.phoneNo, this.smsBody)
              .then(() => {
                this.$notify({ title: 'Success', text: 'An SMS is sent', type: 'success' });
                this.phoneNo = '';
                this.smsBody = '';
              })
              .catch((error) => {
                this.$notify({ title: 'Failed', text: `Cannot send an SMS: ${error}`, type: 'error' });
              });
          }
        });
    },

    // ── Templates ──
    async fetchTemplates() {
      this.templatesLoading = true;
      try {
        const { data } = await axiosTemplate(process.env.VUE_APP_GATEWAY_ENDPOINT).get('v1/email-blast/templates');
        this.templates = data;
      } catch (err) {
        console.error('Failed to fetch templates', err);
      } finally {
        this.templatesLoading = false;
      }
    },

    confirmDelete(template) {
      this.deleteTarget = template;
      this.deleteDialog = true;
    },

    async handleDelete() {
      if (!this.deleteTarget) return;
      this.deleting = true;
      try {
        await axiosTemplate(process.env.VUE_APP_GATEWAY_ENDPOINT).delete(
          `v1/email-blast/templates/${this.deleteTarget.id}`
        );
        this.deleteDialog = false;
        this.deleteTarget = null;
        await this.fetchTemplates();
      } catch (err) {
        console.error('Failed to delete template', err);
      } finally {
        this.deleting = false;
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleDateString();
    },

    // ── Send Blast ──
    async handleSend() {
      this.blastError = null;
      if (!this.blastSubject) {
        this.blastError = 'Subject is required.';
        return;
      }
      if (this.targetMode === 'org' && !this.orgIds.trim()) {
        this.blastError = 'At least one Organization ID is required.';
        return;
      }
      if (this.targetMode === 'users' && !this.userIds.trim()) {
        this.blastError = 'At least one User ID is required.';
        return;
      }
      if (this.blastMode === 'template' && !this.templateId) {
        this.blastError = 'Please select a template.';
        return;
      }
      if (this.blastMode === 'html' && !this.htmlContent.trim()) {
        this.blastError = 'HTML content is required.';
        return;
      }

      let parsedVars;
      if (this.blastMode === 'template' && this.blastVariables.trim()) {
        try {
          parsedVars = JSON.parse(this.blastVariables);
        } catch {
          this.blastError = 'Variables must be valid JSON, e.g. {"name": "John"}';
          return;
        }
      }

      const basePayload = {
        subject: this.blastSubject,
        from: this.blastFrom || undefined,
        reply_to: this.blastReplyTo || undefined,
      };

      if (this.blastMode === 'template') {
        basePayload.template_id = Number(this.templateId);
        if (parsedVars) basePayload.variables = parsedVars;
      } else {
        basePayload.html_content = this.htmlContent;
      }

      // Build the list of requests. Org mode sends one request per organization
      // so the frontend can loop and aggregate the results.
      let requests = [];
      if (this.targetMode === 'users') {
        const userIds = this.userIds
          .split(',')
          .map((s) => Number(s.trim()))
          .filter(Boolean);
        if (!userIds.length) {
          this.blastError = 'At least one valid User ID is required.';
          return;
        }
        requests = [{ payload: { ...basePayload, user_ids: userIds }, label: `Users (${userIds.length})` }];
      } else {
        const orgIds = this.orgIds
          .split(',')
          .map((s) => Number(s.trim()))
          .filter(Boolean);
        if (!orgIds.length) {
          this.blastError = 'At least one valid Organization ID is required.';
          return;
        }
        requests = orgIds.map((id) => ({ payload: { ...basePayload, organization_id: id }, label: `Org ${id}` }));
      }

      this.progress = { current: 0, total: requests.length };
      this.sending = true;

      const blasts = [];
      const errors = [];
      try {
        // Loop sequentially so the backend handles one blast at a time and the
        // modal can show accurate progress.
        for (let i = 0; i < requests.length; i += 1) {
          this.progress.current = i + 1;
          const { payload, label } = requests[i];
          try {
            // eslint-disable-next-line no-await-in-loop
            const { data } = await axiosTemplate(process.env.VUE_APP_GATEWAY_ENDPOINT).post(
              'v1/email-blast/send',
              payload
            );
            blasts.push({ ...data, label });
          } catch (err) {
            errors.push({
              label,
              message:
                (err.response && err.response.data && err.response.data.message) ||
                err.message ||
                'Failed to send blast',
            });
          }
        }

        if (!blasts.length) {
          this.blastError = errors.map((e) => `${e.label}: ${e.message}`).join(' | ') || 'Failed to send blast';
          return;
        }

        this.blastResult = {
          blasts,
          errors,
          totalTargets: blasts.reduce((sum, b) => sum + (b.totalTargets || 0), 0),
          sentCount: blasts.reduce((sum, b) => sum + (b.sentCount || 0), 0),
          failedCount: blasts.reduce((sum, b) => sum + (b.failedCount || 0), 0),
        };
      } finally {
        this.sending = false;
      }
    },
  },
});
</script>

<style scoped></style>
